import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { haversineKm, findNearest } from '@/lib/geo-utils';
import { calculateFullScore, type FullScoreResult } from '@/lib/laadplein-scoring';
import { CORRIDORS } from '@/constants/laadplein-data';

export interface LocatieDetailData {
  locatie: any;
  concurrenten: any[];
  verkeersdata: any[];
  syncLogs: any[];
  nearestCompetitor: { naam: string; afstand_km: number } | null;
  nearestCorridor: { weg: string; traject: string; afstand_km: number; trucks_dag: number } | null;
  autoScore: FullScoreResult | null;
  truckHpc25km: number;
  concurrenten10km: number;
}

export function useLaadpleinLocatieDetail(locatieId: string | undefined) {
  // Fetch the locatie
  const { data: locatie, isLoading: isLocatieLoading } = useQuery({
    queryKey: ['laadplein-locatie', locatieId],
    queryFn: async () => {
      if (!locatieId) return null;
      const { data, error } = await supabase
        .from('laadplein_locaties')
        .select('*')
        .eq('id', locatieId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!locatieId,
  });

  // Fetch competitors for this locatie
  const { data: concurrenten = [] } = useQuery({
    queryKey: ['laadplein-concurrenten-locatie', locatieId],
    queryFn: async () => {
      if (!locatieId) return [];

      // Try location-specific first
      const { data: locData } = await supabase
        .from('laadplein_concurrenten')
        .select('*')
        .eq('locatie_id', locatieId)
        .order('afstand_km', { ascending: true });

      if (locData && locData.length > 0) return locData;

      // Fallback: if locatie has coordinates, find nearby from region data
      if (locatie?.latitude && locatie?.longitude) {
        const { data: allData } = await supabase
          .from('laadplein_concurrenten')
          .select('*')
          .is('locatie_id', null);

        if (allData) {
          return allData
            .map(c => ({
              ...c,
              afstand_km: c.latitude && c.longitude
                ? haversineKm(locatie.latitude, locatie.longitude, c.latitude, c.longitude)
                : null,
            }))
            .filter(c => c.afstand_km != null && c.afstand_km <= 25)
            .sort((a, b) => (a.afstand_km ?? 99) - (b.afstand_km ?? 99));
        }
      }

      return [];
    },
    enabled: !!locatieId && !!locatie,
  });

  // Fetch traffic data
  const { data: verkeersdata = [] } = useQuery({
    queryKey: ['laadplein-verkeersdata-locatie', locatieId],
    queryFn: async () => {
      if (!locatieId) return [];
      const { data, error } = await supabase
        .from('laadplein_verkeersdata')
        .select('*')
        .eq('locatie_id', locatieId)
        .order('datum', { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
    enabled: !!locatieId,
  });

  // Fetch sync logs
  const { data: syncLogs = [] } = useQuery({
    queryKey: ['laadplein-sync-logs-locatie', locatieId],
    queryFn: async () => {
      if (!locatieId) return [];
      const { data, error } = await supabase
        .from('laadplein_data_sync_log')
        .select('*')
        .eq('locatie_id', locatieId)
        .order('synced_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data ?? [];
    },
    enabled: !!locatieId,
  });

  // Derived calculations
  let nearestCompetitor: LocatieDetailData['nearestCompetitor'] = null;
  let nearestCorridor: LocatieDetailData['nearestCorridor'] = null;
  let autoScore: FullScoreResult | null = null;
  let truckHpc25km = 0;
  let concurrenten10km = 0;

  if (locatie) {
    // Nearest competitor
    if (concurrenten.length > 0) {
      const first = concurrenten[0];
      nearestCompetitor = {
        naam: first.naam ?? 'Onbekend',
        afstand_km: first.afstand_km ?? 0,
      };
    }

    // Count truck HPC within 25km and all within 10km
    truckHpc25km = concurrenten.filter(c =>
      (c.aantal_truck_laders ?? 0) > 0 && (c.afstand_km ?? 99) <= 25
    ).length;
    concurrenten10km = concurrenten.filter(c => (c.afstand_km ?? 99) <= 10).length;

    // Nearest corridor
    if (locatie.latitude && locatie.longitude) {
      const nearest = findNearest(locatie.latitude, locatie.longitude, CORRIDORS.map(c => ({
        ...c,
        latitude: c.lat,
        longitude: c.lng,
      })));
      if (nearest) {
        const cor = nearest.item as any;
        nearestCorridor = {
          weg: cor.weg,
          traject: cor.traject,
          afstand_km: nearest.distance,
          trucks_dag: cor.trucks_dag,
        };
      }
    }

    // Auto-score
    autoScore = calculateFullScore({
      afstand_a_n_weg_km: locatie.afstand_a_n_weg_km,
      op_bedrijventerrein: locatie.op_bedrijventerrein,
      toegangsweg_breedte_m: locatie.toegangsweg_breedte_m,
      netaansluiting_kw: locatie.netaansluiting_kw,
      netcongestie_status: locatie.netcongestie_status,
      concurrenten_10km: concurrenten10km,
      truck_hpc_25km: truckHpc25km,
      trucks_per_dag: nearestCorridor?.trucks_dag,
      huur_koop: locatie.huur_koop,
      oppervlakte_m2: locatie.oppervlakte_m2,
    });
  }

  return {
    locatie,
    concurrenten,
    verkeersdata,
    syncLogs,
    nearestCompetitor,
    nearestCorridor,
    autoScore,
    truckHpc25km,
    concurrenten10km,
    isLoading: isLocatieLoading,
  };
}
